import React, { useEffect, useMemo, useState } from 'react';
import { LanguagesLayout, Spin } from '@wajeez-web/components/ui';
import { Languages } from '@wajeez-web/core/utils';
import { Server } from '@wajeez/wajeez.microservices';
import AudioSegmentManager from './AudioSegmentManager';
import {
  editContentEntityMultilingual,
  getContentEntityDetailsByIdMultilingual,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { MediaHelpers } from '@wajeez-web/core/api';
import { useTranslation } from 'react-i18next';

const MultilingualAudioSegmentation = ({
  type,
}: {
  type: Server.ContentEntityTypeDetailedOutput;
}) => {
  const [contentData, setContentData] =
    useState<Server.EditContentEntityByIdV2Input | null>(null);
  const [audioURLs, setAudioURLs] = useState<Record<string, string>>({});

  const { t } = useTranslation();

  const { data, status } = useContentSelector(
    (s) => s.contentEntity.detailsByIdMultilingual
  );
  const profiles = data?.profiles;

  const sortedFields = useMemo(
    () =>
      type.fields?.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ??
      [],
    [type.fields]
  );

  const handleSave = () => {
    if (contentData) {
      editContentEntityMultilingual(contentData, (response) => {
        getContentEntityDetailsByIdMultilingual(contentData.id);
      });
    }
  };

  const handleAddLanguage = (language: Languages) => {
    if (contentData) {
      const addedProfile = {
        ...contentData,
        profiles: contentData.profiles.map((p) => ({
          languageId: language,
          publishDate: undefined,
          title: '',
          fields: sortedFields.map(
            (f): Server.EditContentEntityField => ({
              name: f.name ?? '',
              value: undefined,
            })
          ),
          narratorAudios: type.requireAudio
            ? [{ narratorId: 0, isDefault: true }]
            : [],
          texts: type.requireText ? [{ isDefault: true }] : [],
        })),
      };
      setContentData(addedProfile);
    }
  };

  const handleDeleteLanguage = (language: Languages) => {
    if (contentData) {
      const updatedData = {
        ...contentData,
        profiles: contentData.profiles.filter((p) => p.languageId !== language),
      };
      setContentData(updatedData);
    }
  };

  useEffect(() => {
    if (data && data.profiles) {
      const initialContentData = {
        ...data,
        profiles: data.profiles.map((p) => ({
          title: p.title,
          publishDate: p.publishDate,
          texts: p.texts,
          chapters: p.chapters,
          narratorAudios: p.narratorAudios,
          languageId: p.languageId ?? 0,
          fields: sortedFields.map(
            (f): Server.EditContentEntityField => ({
              name: f.name ?? '',
              value: undefined,
            })
          ),
        })),
      };
      setContentData(initialContentData);
    }
  }, [data, type.fields]);

  useEffect(() => {
    const fetchAudioURLs = async () => {
      const newAudioURLs: Record<string, string> = {};

      for (const profile of profiles || []) {
        const defaultNarrator = profile.narratorAudios?.find(
          (n) => n.isDefault
        );
        if (defaultNarrator?.audioReference) {
          try {
            const url = await MediaHelpers.getMediaUrlByIdAsync(
              defaultNarrator.audioReference
            );
            newAudioURLs[profile.languageId?.toString() || ''] = url;
          } catch (error) {
            console.error('Error fetching audio URL:', error);
          }
        }
      }

      setAudioURLs(newAudioURLs);
    };

    if (profiles?.length) {
      fetchAudioURLs();
    }
  }, [profiles]);

  if (status === 'loading') {
    return (
      <div className="min-h-[300px] flex flex-col justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <LanguagesLayout
        renderPage={(language: Languages) => {
          const currentProfile = profiles?.find(
            (p) => p.languageId === language
          );
          const isRtl =
            currentProfile?.languageId?.toString() === Languages.AR.toString();

          const audioURL = audioURLs[language.toString()];

          return (
            <div>
              <div className="flex">
                <button
                  type="submit"
                  className={`bg-primary p-3 rounded-lg text-[13px] font-medium text-white mr-auto ${
                    currentProfile?.published && 'opacity-50'
                  }`}
                  onClick={handleSave}
                  disabled={currentProfile?.published}
                >
                  {t('buttons.Save')}
                </button>
              </div>
              <div>
                <AudioSegmentManager
                  audioURL={audioURL}
                  contentChapters={currentProfile?.chapters ?? []}
                  originalFullText={currentProfile?.texts ?? []}
                  contentData={data}
                  type={type}
                  languageId={language}
                  isRtl={isRtl}
                  setContentData={setContentData}
                />
              </div>
            </div>
          );
        }}
        defaultLanguages={profiles?.map((s) => s.languageId ?? Languages.AR)}
        onAdd={handleAddLanguage}
        onDelete={handleDeleteLanguage}
      />
    </div>
  );
};

export default MultilingualAudioSegmentation;
